import {useCallback, useEffect, useState} from "react";
import type {MouseEvent, ReactNode} from "react";
import {
  listExecutionEvidence, listExecutionEvents, listExecutionReviews,
  listExecutionRuns, readExecutionProjection, readExecutionRun,
  type ExecutionBatch, type ExecutionEvent, type ExecutionEvidenceReference,
  type ExecutionReview, type ExecutionRun, type ExecutionRunDetail,
  type ExecutionRunProjection
} from "../../api/executionCenter";
import {useAuth} from "../../auth/AuthContext";
import {PageHeader} from "../../components/PageHeader";
import {flowTraceIdFromMap} from "../../utils/flowTrace";
import "./ExecutionCenter.css";

const POLL_MILLIS = 5_000;

export function ExecutionCenter({runId}: {runId?: string}) {
  return runId ? <ExecutionDetail runId={runId} /> : <ExecutionRegistry />;
}

function ExecutionRegistry() {
  const {authorizedFetch} = useAuth();
  const [runs, setRuns] = useState<ExecutionRun[] | null>(null);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const reload = useCallback(async (signal?: AbortSignal) => {
    const next = await listExecutionRuns(authorizedFetch, signal);
    setRuns(next);
    setLastRefresh(new Date());
  }, [authorizedFetch]);

  usePolling(reload, setError);

  return <>
    <PageHeader eyebrow="Operations / Execution Center" title="Execution Center"
      description="Observe governed runs, deterministic batch and part progress, evidence, failures, and human-review state."
      aside={<RefreshButton reload={reload} />} />
    <section className="panel page-panel execution-center-page">
      <div className="execution-center-summary" aria-label="Execution summary">
        <Summary value={String(runs?.length ?? 0)} label="Runs in scope" />
        <Summary value={String(runs?.filter(run => active(run.status)).length ?? 0)} label="Active" />
        <Summary value={String(runs?.filter(run => run.status === "BLOCKED" || run.status === "FAILED").length ?? 0)} label="Attention" />
        <Summary value={lastRefresh ? formatTime(lastRefresh.toISOString()) : "—"} label="Last refresh" />
      </div>
      {error && <p className="form-message error" role="alert">{error}</p>}
      {runs === null && !error && <p role="status">Loading governed executions…</p>}
      {runs?.length === 0 && <div className="empty-state"><h2>No executions yet</h2>
        <p>Task 01 and Backup &amp; Recovery orchestration will publish runs here.</p></div>}
      {runs && runs.length > 0 && <div className="execution-run-grid">
        {runs.map(run => <RunCard key={run.id} run={run} />)}
      </div>}
    </section>
  </>;
}

function ExecutionDetail({runId}: {runId: string}) {
  const {authorizedFetch} = useAuth();
  const [detail, setDetail] = useState<ExecutionRunDetail | null>(null);
  const [events, setEvents] = useState<ExecutionEvent[]>([]);
  const [evidence, setEvidence] = useState<ExecutionEvidenceReference[]>([]);
  const [reviews, setReviews] = useState<ExecutionReview[]>([]);
  const [projection, setProjection] = useState<ExecutionRunProjection | null>(null);
  const [error, setError] = useState("");

  const reload = useCallback(async (signal?: AbortSignal) => {
    const [nextDetail, nextEvents, nextEvidence, nextReviews, nextProjection] = await Promise.all([
      readExecutionRun(authorizedFetch, runId, signal),
      listExecutionEvents(authorizedFetch, runId, signal),
      listExecutionEvidence(authorizedFetch, runId, signal),
      listExecutionReviews(authorizedFetch, runId, signal),
      readExecutionProjection(authorizedFetch, runId, signal)
    ]);
    setDetail(nextDetail);
    setEvents(nextEvents);
    setEvidence(nextEvidence);
    setReviews(nextReviews);
    setProjection(nextProjection);
  }, [authorizedFetch, runId]);

  usePolling(reload, setError);

  if (!detail) {
    return <><PageHeader eyebrow="Operations / Execution Center" title="Execution details"
      description="Loading the persisted execution aggregate." />
      <section className="panel page-panel execution-center-page">
        {error ? <p className="form-message error" role="alert">{error}</p>
          : <p role="status">Loading execution details…</p>}
      </section></>;
  }

  const {run, batches} = detail;
  const flowTraceId = flowTraceIdFromMap(run.metadata);
  return <>
    <PageHeader eyebrow={`Execution Center / ${run.runKey}`} title={run.title}
      description={run.summary || `${run.runType} · ${run.executionMode}`}
      aside={<RefreshButton reload={reload} />} />
    <section className="panel page-panel execution-center-page">
      <a className="execution-back-link" href="/execution-center" onClick={navigate}>← All executions</a>
      {error && <p className="form-message error" role="alert">{error}</p>}
      <div className="execution-detail-header">
        <StatusBadge value={run.status} />
        <div className="execution-progress-copy">
          <strong>{run.progress.completedParts} / {run.progress.totalParts} parts complete</strong>
          <span>{run.progress.percent}% · {run.progress.completedBatches} / {run.progress.totalBatches} batches</span>
        </div>
        <progress value={run.progress.percent} max={100} aria-label="Execution progress">
          {run.progress.percent}%
        </progress>
      </div>
      <dl className="execution-meta-grid">
        {flowTraceId && <Meta label="Flow trace" value={<a href={`/flow-traces/${encodeURIComponent(flowTraceId)}`}>{flowTraceId}</a>} />}
        <Meta label="Run type" value={run.runType} />
        <Meta label="Attempt" value={String(run.attemptNo)} />
        <Meta label="Mode" value={run.executionMode} />
        <Meta label="Current batch" value={run.currentBatchKey || "None"} />
        <Meta label="Current part" value={run.currentPartKey || "None"} />
        <Meta label="Review" value={run.reviewStatus} />
        <Meta label="Version" value={`v${run.version}`} />
        <Meta label="Created by" value={run.createdBy} />
        <Meta label="Updated" value={formatDate(run.updatedAt)} />
      </dl>
      {projection && <GovernedProgress projection={projection} batches={batches} />}
      <div className="execution-detail-layout">
        <section className="execution-primary" aria-label="Batches and parts">
          <h2>Execution plan</h2>
          <div className="execution-batch-list">
            {batches.map(batch => <BatchPanel key={batch.id} batch={batch}
              projection={projection} />)}
          </div>
        </section>
        <aside className="execution-secondary">
          <Timeline events={events} />
          <EvidencePanel evidence={evidence} flowTraceId={flowTraceId} />
          <ReviewPanel reviews={reviews} reviewStatus={run.reviewStatus} />
        </aside>
      </div>
    </section>
  </>;
}

function RunCard({run}: {run: ExecutionRun}) {
  return <article className="execution-run-card">
    <div className="execution-run-heading"><div><small>{run.runKey}</small><h2>{run.title}</h2></div>
      <StatusBadge value={run.status} /></div>
    <p>{run.summary || `${run.runType} · ${run.executionMode}`}</p>
    <progress value={run.progress.percent} max={100} aria-label={`${run.title} progress`} />
    <div className="execution-run-progress"><strong>{run.progress.percent}%</strong>
      <span>{run.progress.completedParts}/{run.progress.totalParts} parts</span></div>
    <dl><Meta label="Current" value={[run.currentBatchKey, run.currentPartKey].filter(Boolean).join(" / ") || "Not started"} />
      <Meta label="Review" value={run.reviewStatus} /><Meta label="Updated" value={formatDate(run.updatedAt)} /></dl>
    <a className="secondary execution-open" href={`/execution-center/${encodeURIComponent(run.id)}`} onClick={navigate}>
      View execution
    </a>
  </article>;
}

function BatchPanel({batch, projection}: {batch: ExecutionBatch; projection: ExecutionRunProjection | null}) {
  return <article className="execution-batch">
    <div className="execution-batch-heading"><div><small>{batch.batchKey}</small><h3>{batch.title}</h3></div>
      <StatusBadge value={batch.status} /></div>
    <ol className="execution-part-list">
      {batch.parts.map(part => {
        const governed = projection?.parts.find(item => item.partId === part.id);
        return <li key={part.id} className="execution-part">
        <span className="execution-part-index">{part.ordinal}</span>
        <div><strong>{part.title}</strong><small>{part.partKey} · {part.partType}</small>
          {part.message && <p>{part.message}</p>}
          {part.failureMessage && <p className="execution-failure"><b>{part.failureCode}</b> {part.failureMessage}</p>}
          {governed && <small className="execution-governance-copy">
            {[governed.workerKey, governed.modelKey].filter(Boolean).join(" · ") || "No AI binding"}
            {` · attempt ${governed.currentAttempt}/${governed.maxAttempts}`}
            {governed.contextWindow ? ` · ${governed.promptTokens ?? 0}/${governed.contextWindow} tokens` : ""}
            {!governed.eligible && governed.blockerReason ? ` · blocked: ${humanStatus(governed.blockerReason)}` : ""}
          </small>}
        </div>
        <StatusBadge value={part.status} />
      </li>})}
    </ol>
  </article>;
}

function GovernedProgress({projection, batches}: {
  projection: ExecutionRunProjection;
  batches: ExecutionBatch[];
}) {
  const sfBatches = ["B01", "B02", "B03", "B04", "B05", "B06", "B07"];
  return <section className="execution-governed-progress" aria-label="Governed execution progress">
    <h2>Governed progress</h2>
    <ol className="execution-phase-strip" aria-label="B01 through B07 progress">
      {sfBatches.map(key => {
        const batch = batches.find(item => item.batchKey === key);
        return <li key={key} data-status={batch?.status ?? "NOT_PLANNED"}>
          <strong>{key}</strong><span>{humanStatus(batch?.status ?? "NOT_PLANNED")}</span>
        </li>;
      })}
    </ol>
    <div className="execution-activity-grid">
      <Activity label="Current activity" value={projection.currentActivity || "No part running"} />
      <Activity label="Next eligible work" value={projection.nextEligibleWork || "None eligible"} />
      <Activity label="Blocker / rule" value={projection.blockerReason
        ? `${humanStatus(projection.blockerReason)} · ${projection.governingRule}` : "No active blocker"} />
      <Activity label="Human gate" value={projection.humanGate ? "Named-human review required" : "Not currently waiting"} />
    </div>
    <div className="execution-axis-grid">
      <Axis label="Engineering" axis={projection.engineering} />
      <Axis label="Testing" axis={projection.testing} />
      <Axis label="Evidence" axis={projection.evidence} />
      <Axis label="Human gate" axis={projection.humanGateProgress} />
    </div>
    <p className="execution-completion-evidence">
      Immutable completion evidence: <strong>{projection.completionSnapshotPresent ? "recorded" : "not yet recorded"}</strong>
    </p>
    {projection.retryHistory.length > 0 && <details>
      <summary>Retry and attempt history ({projection.retryHistory.length})</summary>
      <ol className="execution-retry-history">{projection.retryHistory.map(attempt =>
        <li key={attempt.id}><strong>Attempt {attempt.attemptNo}: {humanStatus(attempt.outcome)}</strong>
          <span>{attempt.failureCode || "Governed retry record"} · {formatDate(attempt.recordedAt)}</span></li>)}</ol>
    </details>}
  </section>;
}

function Activity({label, value}: {label: string; value: string}) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function Axis({label, axis}: {label: string; axis: {completed: number; total: number; percent: number}}) {
  return <div><span>{label}</span><progress value={axis.percent} max={100} aria-label={`${label} progress`} />
    <strong>{axis.completed}/{axis.total} · {axis.percent}%</strong></div>;
}

function Timeline({events}: {events: ExecutionEvent[]}) {
  const recent = [...events].reverse().slice(0, 12);
  return <section className="execution-side-panel"><h2>Recent activity</h2>
    {recent.length === 0 ? <p>No persisted events yet.</p> : <ol className="execution-event-list">
      {recent.map(event => <li key={event.id}><time>{formatTime(event.occurredAt)}</time><div>
        <strong>{event.eventType}</strong><span>{[event.batchKey, event.partKey].filter(Boolean).join(" / ") || "Run"}</span>
      </div><StatusBadge value={event.status} /></li>)}
    </ol>}
  </section>;
}

function EvidencePanel({evidence, flowTraceId}: {evidence: ExecutionEvidenceReference[]; flowTraceId: string | null}) {
  return <section className="execution-side-panel"><h2>Evidence</h2>
    {flowTraceId && <p><strong><a href={`/flow-traces/${encodeURIComponent(flowTraceId)}`}>Flow {flowTraceId}</a></strong></p>}
    {evidence.length === 0 ? <p>No evidence references recorded.</p> : <ul className="execution-evidence-list">
      {evidence.slice(-8).reverse().map(item => <li key={item.id}><strong>{item.evidenceType}</strong>
        <span>{item.sourceType} · {humanBytes(item.byteLength)}</span><code>{item.sha256.slice(0, 16)}…</code>
        <small>{item.sourceReference}</small></li>)}
    </ul>}
  </section>;
}

function ReviewPanel({reviews, reviewStatus}: {reviews: ExecutionReview[]; reviewStatus: string}) {
  return <section className="execution-side-panel"><h2>Human review</h2>
    <p><StatusBadge value={reviewStatus} /></p>
    {reviews.length === 0 ? <p>No review decision recorded.</p> : reviews.slice(-3).reverse().map(review =>
      <article className="execution-review" key={review.id}><strong>{review.decision}</strong>
        <p>{review.rationale}</p><small>{review.reviewerSubject} · {formatDate(review.reviewedAt)}</small></article>)}
  </section>;
}

function RefreshButton({reload}: {reload: (signal?: AbortSignal) => Promise<void>}) {
  const [working, setWorking] = useState(false);
  return <button type="button" className="secondary" disabled={working} onClick={() => {
    setWorking(true);
    void reload().finally(() => setWorking(false));
  }}>{working ? "Refreshing…" : "Refresh"}</button>;
}

function Summary({value, label}: {value: string; label: string}) {
  return <div><strong>{value}</strong><span>{label}</span></div>;
}

function Meta({label, value}: {label: string; value: ReactNode}) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function StatusBadge({value}: {value: string}) {
  return <span className={`badge execution-status ${tone(value)}`}>{humanStatus(value)}</span>;
}

function usePolling(
  reload: (signal?: AbortSignal) => Promise<void>,
  setError: (message: string) => void
) {
  useEffect(() => {
    const controller = new AbortController();
    let timer = 0;
    const run = async () => {
      try {
        await reload(controller.signal);
        if (!controller.signal.aborted) setError("");
      } catch (reason) {
        if (!controller.signal.aborted) setError(message(reason));
      } finally {
        if (!controller.signal.aborted) timer = window.setTimeout(run, POLL_MILLIS);
      }
    };
    void run();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [reload, setError]);
}

function navigate(event: MouseEvent<HTMLAnchorElement>) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  window.history.pushState({}, "", event.currentTarget.href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function active(status: string) {
  return ["QUEUED", "RUNNING", "VALIDATING", "AWAITING_HUMAN_REVIEW"].includes(status);
}

function tone(status: string) {
  if (["COMPLETED", "APPROVED"].includes(status)) return "safe";
  if (["FAILED", "REJECTED", "CANCELLED"].includes(status)) return "deny";
  if (["BLOCKED", "REVISION_REQUIRED", "COMPLETED_WITH_WARNINGS"].includes(status)) return "warning";
  if (["RUNNING", "VALIDATING", "PREFLIGHT", "QUEUED", "PENDING", "AWAITING_HUMAN_REVIEW"].includes(status)) return "active";
  return "";
}

function humanStatus(value: string) {
  return value.toLowerCase().replaceAll("_", " ");
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {dateStyle: "medium", timeStyle: "short"}).format(new Date(value));
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {hour: "2-digit", minute: "2-digit", second: "2-digit"}).format(new Date(value));
}

function humanBytes(value: number) {
  if (value < 1_024) return `${value} B`;
  if (value < 1_048_576) return `${(value / 1_024).toFixed(1)} KiB`;
  if (value < 1_073_741_824) return `${(value / 1_048_576).toFixed(1)} MiB`;
  return `${(value / 1_073_741_824).toFixed(1)} GiB`;
}

function message(reason: unknown) {
  return reason instanceof Error ? reason.message : "Execution Center request failed";
}
